import { ReactElement, useEffect, useState } from 'react';
import { useConditionalEffect, usePrevious } from 'react-delta';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { History } from 'history';
import { alertsConfigState } from 'components/alerts/alertsconfig.state';
import { isEmpty, isEqual } from 'lodash';

import { RouteComponentProps, withRouter } from 'react-router-dom';
import BootstrapNavbar from 'react-bootstrap/Navbar';
import BootstrapNav from 'react-bootstrap/Nav';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { AccessLevel } from 'common/graphqlTypes';
import log, { setCurrentUserAccount } from 'common/ScalyrLogger';
import { getNextHour, getPreviousDay } from 'common/util/time';
import { logoState, navbarVariantState } from 'components/common/Managers/SettingsManager.state';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';

import Link from 'components/common/Link/Link';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';

import {
  accountDataState,
  featurePermissionsState,
  hindsightRetentionState,
  userDataState,
} from 'components/account/account.state';
import { HINDSIGHT_RETENTION_QUERY } from 'components/billingAndUsage/queries';

import NavBarDocsDropdown from './NavBarDocsDropdown';
import NavBarDashboardsDropdown from './NavBarDashboardsDropdown';
import NavBarSearchDropdown from './NavBarSearchDropdown';
import NavBarUserMenu from './NavBarUserMenu';
import AccountBadge from './AccountBadge';
import { isFullScreenModeState } from './MainNavBar.state';

import { USER_DATA_QUERY } from './queries';

import './MainNavBar.scss';

type MainNavBarProps = {
  isTeamTokenMismatch?: boolean;
};

// TODO: DPVZ-2189 - These should be refactored out, as a common component shouldn't have a dependency on a plugin

/**
 * The Scalyr navbar component with overrides for bootstrap styles.
 *
 * @returns {Fragment}        The main navbar component for the Scalyr app
 */
const MainNavBar = ({
  history,
  isTeamTokenMismatch = false,
}: RouteComponentProps & MainNavBarProps): ReactElement | null => {
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [activePath, setActivePath] = useState(history.location.pathname);
  const isFullScreenMode = useRecoilValue(isFullScreenModeState);
  const featurePermissions = useRecoilValue(featurePermissionsState);
  const { logoUrl, logoWidth, favIcon } = useRecoilValue(logoState);
  const navbarVariant = useRecoilValue(navbarVariantState);

  const setUserData = useSetRecoilState(userDataState);
  const accountData = useRecoilValue(accountDataState);
  const [alertsConfig, setAlertsConfig] = useRecoilState(alertsConfigState);
  const { S1ConsoleURL } = accountData;

  /**
   * Get the active class string with a path name.
   *
   * @param pathName       - Path name e.g. '/logStart'
   * @returns classNames   - This returns a class string based on the path change.
   */
  const getActiveClass = (pathName: K.ROUTE): string => classNames({ active: activePath === pathName });

  useEffect(() => {
    // Listen to history changes, and set the route state.
    // TODO: Remove casting once we update to newer version of react-router as `history` typing is not correct
    (history as History).listen(({ location }) => {
      setNavbarExpanded(false);
      log.info([`path name changed to ${location.pathname}`, location]);
      setActivePath(location.pathname);
    });
  }, [history]);

  const { loading, error, data } = useQuery(USER_DATA_QUERY);

  const { alertsConfig: newAlertsConfig, ...userData } = data || {};

  const prevUserData = usePrevious(userData);
  useConditionalEffect(() => {
    setUserData(userData);
    setCurrentUserAccount(userData.account);
  }, !isEmpty(userData) && userData !== prevUserData);

  useConditionalEffect(() => {
    setAlertsConfig(newAlertsConfig);
  }, !isEqual(alertsConfig, newAlertsConfig));

  // FRONT-6510: get Hindsight retention period so we can check if it's been fully configured
  // The request will be fast since it only fetches retentionDays
  const setHindsightRetention = useSetRecoilState(hindsightRetentionState);
  const isHindsightLabEnabled = useIsLabEnabled(K.LAB.HINDSIGHT);
  const { data: hsResult } = useQuery(HINDSIGHT_RETENTION_QUERY, {
    skip: !isHindsightLabEnabled,
    variables: { startDate: getPreviousDay(30), endDate: getNextHour() },
  });
  const prevHindsightRetention = usePrevious(hsResult?.hindsightData?.retentionDays);
  useConditionalEffect(() => {
    setHindsightRetention(hsResult?.hindsightData?.retentionDays);
  }, hsResult?.hindsightData?.retentionDays > 0 && hsResult?.hindsightData?.retentionDays !== prevHindsightRetention);

  // use SentinelOne favicon for Singularity user
  const favIconEl = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  const favIconUrl = favIconEl?.href;
  const slashIdx = favIconUrl?.lastIndexOf('/');
  const curFavIcon = slashIdx > 0 && favIconUrl.substring(slashIdx + 1);
  if (curFavIcon && favIcon && curFavIcon !== favIcon) {
    favIconEl.href = favIconUrl.substring(0, slashIdx + 1) + favIcon;
  }

  if (loading) {
    // TODO: use LoadingPlaceholder when debounce is implemented
    return null;
  }

  // An error in USER_DATA_QUERY means the app can't function. The router will suspend rendering while it waits on
  // critical user data to be available in global state, so we create an error UX here.
  if (error) {
    // This error UX is unnecessary when the user visits a link with a mismatched team token, since the user
    // will have the {@see TeamSwitchDialog} UI to interact with.
    if (isTeamTokenMismatch) {
      return null;
    } else {
      return (
        <div className="NavBarError">
          <ErrorPlaceholder error={error} />
        </div>
      );
    }
  }

  if (isFullScreenMode) {
    return null;
  }

  let navbarLogs = null;
  if (!!featurePermissions.logs && featurePermissions.logs !== AccessLevel.DENY) {
    navbarLogs = (
      <BootstrapNav.Item className={getActiveClass(K.ROUTE.LOG_START)}>
        <BootstrapNav.Link as={Link} href={K.ROUTE.LOG_START} testId={ELEMENT.navLogsButton}>
          {K.ROUTE_NAME.LOGS}
        </BootstrapNav.Link>
      </BootstrapNav.Item>
    );
  }

  let navbarSearch = null;
  if (!!featurePermissions.search && featurePermissions.search !== AccessLevel.DENY) {
    navbarSearch = (
      <NavBarSearchDropdown
        isActive={activePath === K.ROUTE.EVENTS}
        testId={ELEMENT.navSearchButton}
        sharedSearchPermissions={featurePermissions.sharedSearches}
      />
    );
  }

  let navbarDashboards = null;
  if (!!featurePermissions.dashboards && featurePermissions.dashboards !== AccessLevel.DENY) {
    navbarDashboards = (
      <NavBarDashboardsDropdown
        isActive={activePath === K.ROUTE.DASHBOARD}
        readOnly={featurePermissions.dashboards === AccessLevel.READ}
        testId={ELEMENT.navDashboardsButton}
      />
    );
  }

  let starCustomRules = null;
  if (accountData.isSingularityXDRUser && S1ConsoleURL) {
    const RULES_PATH = 'sentinels/custom-rules';
    starCustomRules = (
      <BootstrapNav.Item>
        <BootstrapNav.Link
          as={Link}
          href={`${S1ConsoleURL}/${RULES_PATH}`}
          target={'_blank'}
          testId={ELEMENT.navStarCustomRules}
        >
          Star Custom Rules
        </BootstrapNav.Link>
      </BootstrapNav.Item>
    );
  }

  let navbarAlerts = null;
  if (!!featurePermissions.alerts && featurePermissions.alerts !== AccessLevel.DENY) {
    navbarAlerts = (
      <BootstrapNav.Item className={getActiveClass(K.ROUTE.ALERTS)}>
        <BootstrapNav.Link as={Link} href={K.ROUTE.ALERTS} testId={ELEMENT.navAlertsButton}>
          {K.ROUTE_NAME.ALERTS}
        </BootstrapNav.Link>
      </BootstrapNav.Item>
    );
  }

  // NOTE: we use the "dark" variant so that the hamburger icon in the collapsed
  // nav bar is legible. So far, we're always some form of dark. If that changes,
  // we'll need to conditionally change or exclude the attribute.

  return (
    <>
      <BootstrapNavbar expanded={navbarExpanded} expand="lg" className="navbar-scalyr" variant={navbarVariant}>
        <BootstrapNavbar.Brand as={Link} href={K.ROUTE.ROOT}>
          <img alt={K.PRODUCT_NAME} src={logoUrl} data-test-id={ELEMENT.navScalyrLogo} style={{ width: logoWidth }} />
        </BootstrapNavbar.Brand>
        <BootstrapNavbar.Toggle onClick={(): void => setNavbarExpanded(!navbarExpanded)} />
        <BootstrapNavbar.Collapse>
          <BootstrapNav className="Navbar--MainTabs mr-auto">
            {navbarLogs}
            {navbarSearch}
            {navbarDashboards}
            {starCustomRules}
            {navbarAlerts}
            <NavBarDocsDropdown testId={ELEMENT.navDocsButton} />
          </BootstrapNav>

          <BootstrapNav className="user-navbar-nav">
            <AccountBadge />
            <NavBarUserMenu />
          </BootstrapNav>
        </BootstrapNavbar.Collapse>
      </BootstrapNavbar>
    </>
  );
};

export default withRouter(MainNavBar);
